import { Autocomplete, Box, Button, IconButton, TextField, Typography } from '@mui/material'
import { FC, useState } from 'react'
import { FFModal } from '~/shared/ui'
import CloseIcon from '@mui/icons-material/Close'
import { CarePlanItem, CarePlanApiResponse } from '../carePlanTypes'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutline from '@mui/icons-material/DeleteOutlineOutlined'
import {
  carePlanV2,
  carePlanAction,
  CarePlanEducationResourceListResponse,
} from '@fireflyhealth/api'
import { InternalUseOnlyCheckbox } from '../InternalUseOnlyCheckbox'
import { useMutation } from '~/shared/hooks/useMutation'

/** Education resource from the generated API (snake_case) */
type EducationResourceItem = CarePlanEducationResourceListResponse[number]

/** Options shown in the dropdown: { id, title, url } from the API */
type EducationResourceOption = Pick<EducationResourceItem, 'id' | 'title' | 'url'>

/** Unsaved resources don't have an id yet */
type UnsavedEducationResource = Pick<EducationResourceItem, 'title' | 'url'>

/** Either a saved resource (with id) or unsaved resource (without id) */
type AnyEducationResource = EducationResourceOption | UnsavedEducationResource

const NEW_RESOURCE: UnsavedEducationResource = {
  title: '',
  url: '',
}

function appendNewResource(resources: AnyEducationResource[]): AnyEducationResource[] {
  return [...resources, NEW_RESOURCE]
}

function replaceResource(
  resources: AnyEducationResource[],
  resource: AnyEducationResource,
  index: number,
) {
  const clone = [...resources]
  clone.splice(index, 1, resource)
  return clone
}

function removeResource(resources: AnyEducationResource[], index: number) {
  const clone = [...resources]
  clone.splice(index, 1)
  return clone
}

type ResourceRowProps = {
  index: number
  options: EducationResourceOption[]
  resource: AnyEducationResource
  onChange: (resource: AnyEducationResource, index: number) => void
  onRemove: (index: number) => void
  saving: boolean
}

const ResourceRow: FC<ResourceRowProps> = ({
  index,
  options,
  resource,
  onChange,
  onRemove,
  saving,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', mb: 2 }}>
      <Box>
        <Autocomplete<AnyEducationResource, false, true, false>
          disabled={saving}
          value={resource}
          options={options}
          onChange={(_, value) => {
            onChange(value, index)
          }}
          isOptionEqualToValue={(option, value) => {
            return 'id' in option && 'id' in value ? option.id === value.id : false
          }}
          disableClearable={true}
          sx={{ width: 400 }}
          getOptionLabel={option => option.title}
          renderOption={({ key, ...props }, option) => {
            return (
              <li key={key} {...props} style={{ wordBreak: 'break-word' }}>
                <Typography sx={{ fontWeight: 'bold' }}>
                  {option.title}
                  <Typography>{option.url}</Typography>
                </Typography>
              </li>
            )
          }}
          renderInput={params => <TextField {...params} label="Choose a Resource" />}
        />
      </Box>
      <Box sx={{ mt: 1, ml: 1 }}>
        <IconButton onClick={() => onRemove(index)} disabled={saving}>
          <DeleteOutline />
        </IconButton>
      </Box>
    </Box>
  )
}

const CaseBasedCarePlanModal: FC<{
  carePlan: CarePlanItem
  closeModal: () => void
  onCareplanCreateOrUpdate: (carePlan: CarePlanItem | CarePlanApiResponse) => void
  educationResourceOptions: EducationResourceOption[]
  edit: boolean
}> = props => {
  const { carePlan, closeModal, onCareplanCreateOrUpdate } = props
  const [notes, setNotes] = useState<string>(carePlan.notes ?? '')
  // "Internal use only" hides the care plan from the member (visible_to_patient: false).
  // Default unchecked for the review-and-send flow; seed from the plan when editing.
  const [internalOnly, setInternalOnly] = useState<boolean>(
    props.edit ? !(carePlan.visible_to_patient ?? false) : false,
  )

  const [resources, setResources] = useState<AnyEducationResource[]>(
    (carePlan.care_plan_education_resources ?? []).map(r => r.education_resource),
  )

  const [resourceOptions, _] = useState<EducationResourceOption[]>(
    props.educationResourceOptions /* [] */,
  )

  const resourceIds = resources
    .map(r => {
      if ('id' in r) {
        return r.id
      }
    })
    .filter(x => !!x)

  const unusedResourceOptions = resourceOptions.filter(o => !resourceIds.includes(o.id))

  const handleUpdateNotes = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event) {
      setNotes(event.target?.value)
      if (event.target.value !== '') {
        setNotesError(false)
      } else {
        setNotesError(true)
      }
    }
  }

  const onAddMoreButtonClick = () => {
    setResources(prev => appendNewResource(prev))
  }

  const onResourceChange = (resource: AnyEducationResource, index: number) => {
    setResources(prev => replaceResource(prev, resource, index))
  }

  const onResourceDelete = (index: number) => {
    setResources(prev => removeResource(prev, index))
  }

  const [notesError, setNotesError] = useState<boolean>(false)

  const updateNotes = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.target.value == '') {
      setNotesError(true)
    }
  }

  const saveCarePlan = useMutation(
    async () => {
      const currentVisibility = carePlan.visible_to_patient ?? false
      // The "Internal use only" checkbox governs visibility: checked → hidden.
      const intendedVisibility = !internalOnly

      const carePlanEducationResourcesPayload = resources
        .filter(resource => resource.title !== '')
        .map(resource => ({
          education_resource: { title: resource.title, url: resource.url },
        }))

      const { data: apiResponse } = await carePlanV2.partialUpdate(
        { id: carePlan.id },
        {
          data: {
            notes,
            patient: carePlan.patient,
            title: carePlan.title,
            care_plan_education_resources: carePlanEducationResourcesPayload,
          },
        },
      )

      let updatedCarePlanResponse: CarePlanItem | CarePlanApiResponse = apiResponse
      // Saving always activates the plan; call the action endpoint on a status
      // change (draft → active) or when visibility changes on an active plan.
      const statusIsChanging = carePlan.status !== 'active'
      const needsVisibilitySync =
        intendedVisibility !== currentVisibility && carePlan.status === 'active'
      if (statusIsChanging || needsVisibilitySync) {
        const allTasks = carePlan.tasks ?? []
        const allCases = carePlan.cases ?? []
        const { data: actionResponse } = await carePlanAction.partialUpdate(
          { id: carePlan.id },
          {
            data: {
              action: 'active',
              tasks: allTasks.map(task => ({
                id: task.id,
                is_complete: task.is_complete ?? false,
              })),
              cases: allCases.map(c => ({ id: c.id })),
              visible_to_patient: intendedVisibility,
            },
          },
        )
        updatedCarePlanResponse = actionResponse
      }
      onCareplanCreateOrUpdate(updatedCarePlanResponse)
      closeModal()
    },
    { error: 'Failed to save the care plan' },
  )
  const saving = saveCarePlan.loading

  // A member-facing message is only required when the plan will be visible to the member —
  // saving as internal-only can have empty notes. Checked before the request, not inside it.
  const handleSaving = () => {
    if (!internalOnly && notes.trim() == '') {
      setNotesError(true)
      return
    }
    saveCarePlan.run()
  }

  return (
    <FFModal open={true}>
      <Box>
        <Box sx={{ m: 2 }}>
          <Typography variant="h6">{'Edit Care Plan'}</Typography>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            {'Staying Healthy'}
          </Typography>
        </Box>
        <Box sx={{ m: 2 }}>
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            {'From Your Clinician'}
          </Typography>
          <TextField
            id="outlined-basic"
            variant="outlined"
            value={notes}
            onChange={e => handleUpdateNotes(e)}
            multiline={true}
            fullWidth
            placeholder="Add goals or a personalized message"
            disabled={saving}
            error={notesError}
            helperText={notesError && 'Goals or personalized message required'}
            onBlur={updateNotes}
          />
        </Box>
        <Box sx={{ m: 2 }}>
          <InternalUseOnlyCheckbox
            checked={internalOnly}
            onChange={checked => {
              setInternalOnly(checked)
              // Notes are only required when sending to the member, so clear
              // any stale validation error when switching to internal-only.
              if (checked) setNotesError(false)
            }}
            disabled={saving}
          />
        </Box>
        <Box sx={{ m: 2 }}>
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            {'Resources'}
          </Typography>
          <>
            {resources.map((resource, index) => (
              <ResourceRow
                key={`educationResource_${index}`}
                index={index}
                options={unusedResourceOptions}
                resource={resource}
                onChange={onResourceChange}
                onRemove={onResourceDelete}
                saving={saving}
              />
            ))}
          </>
          <Box sx={{ alignSelf: 'center' }}>
            <IconButton
              color="primary"
              disabled={saving}
              onClick={onAddMoreButtonClick}
              sx={theme => ({
                ml: '-1rem !important',
                mb: 0.5,
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: theme.palette.primary[700],
                },
                borderRadius: 'unset',
              })}
            >
              <AddIcon color="inherit" sx={{ fontSize: '1.4rem', fontWeight: 500 }} />
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>Add more</Typography>
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ m: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
            <Button
              onClick={closeModal}
              variant="text"
              disabled={saving}
              size="small"
              sx={{
                borderRadius: 10,
                fontSize: '1.4rem',
                maxHeight: '75%',
                width: '20%',
                marginRight: 1,
              }}
            >
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Cancel'}</Typography>
            </Button>
            <Button
              disabled={saving}
              onClick={handleSaving}
              variant="contained"
              size="small"
              sx={{
                borderRadius: 10,
                fontSize: '1.4rem',
                maxHeight: '75%',
                width: '30%',
              }}
            >
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>
                {internalOnly ? 'Save' : 'Send to member'}
              </Typography>
            </Button>
          </Box>
        </Box>
        <Box sx={{ m: 2 }}>
          <IconButton
            sx={{
              position: 'absolute',
              zIndex: 100,
              top: theme => theme.spacing(1),
              right: theme => theme.spacing(1),
            }}
            onClick={closeModal}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
    </FFModal>
  )
}

export default CaseBasedCarePlanModal
